import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Car, 
  ChevronDown,
  ChevronUp,
  HelpCircle,
  ChevronRight
} from "lucide-react";

export const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (key) => {
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="faq-page">
      <Helmet>
        <title>Frequently Asked Questions | Moto Money Finance</title>
        <meta name="description" content="Find answers to common questions about vehicle financing, applying, credit situations, trade-ins, and more at Moto Money Finance." />
      </Helmet>

      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo" data-testid="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          <nav className="nav-links">
            <Link to="/how-it-works" className="nav-link">How It Works</Link>
            <Link to="/apply" className="btn-apply" data-testid="header-apply-btn">Apply Now</Link>
          </nav>
        </div>
      </header>

      <section className="faq-hero">
        <div className="faq-hero-content">
          <HelpCircle size={48} />
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about our financing process, credit situations, and more.</p>
        </div>
      </section>

      <section className="faq-content-section">
        <div className="faq-container">
          
          {/* Applying */}
          <div className="faq-category">
            <h2>Applying</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['a1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('a1')}>
                  <span>How do I apply for vehicle financing?</span>
                  {openItems['a1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['a1'] && <div className="faq-answer"><p>You can apply online through our website by clicking 'Apply Now'. The application takes just a few minutes to complete. You'll provide your contact information, vehicle preferences, and basic financial details. Once submitted, our team will review your application and reach out to discuss your options.</p></div>}
              </div>
              <div className={`faq-item ${openItems['a2'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('a2')}>
                  <span>What information do I need to apply?</span>
                  {openItems['a2'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['a2'] && <div className="faq-answer"><p>To apply, you'll need to provide your contact information (name, phone, email, address), your vehicle preferences (new or used, type, budget), and some basic financial information (employment status, housing situation).</p></div>}
              </div>
              <div className={`faq-item ${openItems['a3'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('a3')}>
                  <span>Is there a fee to apply?</span>
                  {openItems['a3'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['a3'] && <div className="faq-answer"><p>No, there is no fee to submit an application with Moto Money Finance. Our service is free to applicants.</p></div>}
              </div>
            </div>
          </div>

          {/* Credit Situations */}
          <div className="faq-category">
            <h2>Credit Situations</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['c1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('c1')}>
                  <span>Can I get approved if I have bad credit or no credit?</span>
                  {openItems['c1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['c1'] && <div className="faq-answer"><p>We work with Canadians in all credit situations, including those with bad credit, no credit history, or who are rebuilding their credit. Each situation is different, and our team will work with you to explore available options based on your specific circumstances.</p></div>}
              </div>
              <div className={`faq-item ${openItems['c2'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('c2')}>
                  <span>What if I've had a bankruptcy or consumer proposal?</span>
                  {openItems['c2'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['c2'] && <div className="faq-answer"><p>We understand that financial challenges happen. We work with many customers who have been through bankruptcy or consumer proposals. Our team can discuss what options may be available based on your current situation.</p></div>}
              </div>
              <div className={`faq-item ${openItems['c3'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('c3')}>
                  <span>Will applying affect my credit score?</span>
                  {openItems['c3'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['c3'] && <div className="faq-answer"><p>Submitting an enquiry through our website does not affect your credit score. If you proceed with a formal financing application through one of our lending partners, they may perform a credit check as part of their approval process, which could impact your score.</p></div>}
              </div>
            </div>
          </div>

          {/* Down Payments */}
          <div className="faq-category">
            <h2>Down Payments</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['d1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('d1')}>
                  <span>Do I need a down payment?</span>
                  {openItems['d1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['d1'] && <div className="faq-answer"><p>Down payment requirements vary depending on your credit situation, the vehicle, and the lender. Some customers may qualify with $0 down, while others may need to provide a down payment. Our team will discuss specific requirements based on your situation.</p></div>}
              </div>
              <div className={`faq-item ${openItems['d2'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('d2')}>
                  <span>Can a trade-in be used as a down payment?</span>
                  {openItems['d2'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['d2'] && <div className="faq-answer"><p>Yes, if you have a vehicle to trade in, the value can often be applied toward your down payment. Visit our Trade-In page to submit details about your current vehicle.</p></div>}
              </div>
            </div>
          </div>

          {/* After Applying */}
          <div className="faq-category">
            <h2>After Applying</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['p1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('p1')}>
                  <span>What happens after I submit my application?</span>
                  {openItems['p1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['p1'] && <div className="faq-answer"><p>After you submit your application, our team will review your information and contact you within 1-2 business days. We'll discuss your vehicle needs, answer your questions, and explain the financing options that may be available to you.</p></div>}
              </div>
              <div className={`faq-item ${openItems['p2'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('p2')}>
                  <span>Is approval guaranteed?</span>
                  {openItems['p2'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['p2'] && <div className="faq-answer"><p>While we work hard to help every customer, approval is not guaranteed and depends on various factors including your credit history, income, and the specific lender's requirements. Our team will be upfront about what options are realistically available to you.</p></div>}
              </div>
            </div>
          </div>

          {/* Referrals */}
          <div className="faq-category">
            <h2>Referrals</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['r1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('r1')}>
                  <span>How does the referral program work?</span>
                  {openItems['r1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['r1'] && <div className="faq-answer"><p>If you know someone looking for vehicle financing, you can refer them through our Refer a Friend page. Simply provide their contact information (with their permission) and your own details. Our team will reach out to them about financing options.</p></div>}
              </div>
              <div className={`faq-item ${openItems['r2'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('r2')}>
                  <span>Do I need my friend's permission to refer them?</span>
                  {openItems['r2'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['r2'] && <div className="faq-answer"><p>Yes, absolutely. Before submitting a referral, you must confirm that your friend has given you permission to share their contact details and has agreed to be contacted by Moto Money Finance.</p></div>}
              </div>
            </div>
          </div>

          {/* Trade-Ins */}
          <div className="faq-category">
            <h2>Trade-Ins</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['t1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('t1')}>
                  <span>Can I trade in my current vehicle?</span>
                  {openItems['t1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['t1'] && <div className="faq-answer"><p>Yes! If you have a vehicle you'd like to trade in, visit our Trade-In page to submit details about your vehicle. Our team will review the information and discuss how your trade-in can be factored into your next vehicle purchase.</p></div>}
              </div>
              <div className={`faq-item ${openItems['t2'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('t2')}>
                  <span>Will I get a guaranteed trade-in value?</span>
                  {openItems['t2'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['t2'] && <div className="faq-answer"><p>The trade-in enquiry helps our team understand your vehicle. An actual trade-in value will be determined after a more detailed assessment. The enquiry does not guarantee any specific value.</p></div>}
              </div>
            </div>
          </div>

          {/* Delivery */}
          <div className="faq-category">
            <h2>Delivery</h2>
            <div className="faq-list">
              <div className={`faq-item ${openItems['e1'] ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleItem('e1')}>
                  <span>Do you offer delivery?</span>
                  {openItems['e1'] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openItems['e1'] && <div className="faq-answer"><p>We serve customers across Manitoba and northern communities. Ask our team about delivery options for your community. Delivery availability varies based on location.</p></div>}
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="faq-cta-section">
        <div className="faq-container">
          <div className="faq-cta-card">
            <h2>Still Have Questions?</h2>
            <p>Our team is here to help. Reach out and we'll be happy to answer any questions you have about the financing process.</p>
            <div className="faq-cta-buttons">
              <a href="mailto:sales@motomoneyfinance.com" className="btn btn-secondary">Email Us</a>
              <Link to="/apply" className="btn btn-primary">Apply Now <ChevronRight size={18} /></Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <Link to="/" className="footer-logo"><Car size={28} /><span>Moto Money<strong>Finance</strong></span></Link>
            <p>Driving Made Easy</p>
          </div>
          <div className="footer-links">
            <h4>Quick Links</h4>
            <Link to="/apply">Apply Now</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
          <div className="footer-contact">
            <h4>Contact Us</h4>
            <a href="mailto:sales@motomoneyfinance.com">sales@motomoneyfinance.com</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p><Link to="/privacy">Privacy Policy</Link></p>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
